import axios from 'axios';
import { padStart } from 'lodash';
import auth from '@client/components/auth';
import { Download } from 'generated/graphql';
import _ from 'lodash';

export function secondsToHHMMSS(seconds: number): string {
  seconds = Math.round(seconds);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  const formattedTime =
    padStart(hours.toString(), 2, '0') +
    ':' +
    padStart(minutes.toString(), 2, '0') +
    ':' +
    padStart(remainingSeconds.toString(), 2, '0');
  return formattedTime;
}

export type MuxVideo = {
  id: string;
  influencerId: number;
  thumbnail: string;
  video: string;
  mimeType: 'application/x-mpegURL';
  width: number;
  height: number;
  uploadLink: string;
  downloadLink?: Download;
  duration: number;
  type: string;
  title: string;
  aspectRatio: string;
  resolution: string;
  status:
    | 'UPLOAD_URL_CREATED'
    | 'PROCESSING_STARTED'
    | 'PROCESSING_ENCODE_FINISHED'
    | 'PROCESSING_FINISHED';
  fileSize: number;
  deletedAt: string | null;
  createdAt: string;
};

const generateMuxSignedUrl = (data: {
  influencerId: number;
  fileSize: number;
  title: string;
  thumbnailPosition?: string;
  bucketName?: string;
}): Promise<{
  signedURL: string;
  videoId: number;
}> =>
  axios
    .post(`${import.meta.env.VITE_MUX_API_URL}/signed-url`, data, {
      headers: {
        Authorization: auth.getToken(),
      },
    })
    .then((response) => {
      if (response.status === 401) {
        throw new Error('auth');
      }
      return response.data;
    })
    .then((result) => ({
      signedURL: result.signedURL,
      videoId: result.video.id,
    }));

const chunkSize = 5120;

const fetchVideo = async (videoId: number) =>
  axios
    .get<{ message: string; video: MuxVideo }>(
      `${import.meta.env.VITE_MUX_API_URL}/video/${videoId}`,
      {
        headers: {
          Authorization: auth.getToken(),
        },
      },
    )
    .then((res) => res.data);

const leadingZeroFormatter = new Intl.NumberFormat(undefined, {
  minimumIntegerDigits: 2,
});

const millisecondsFormatter = new Intl.NumberFormat(undefined, {
  minimumIntegerDigits: 3,
});

export const getVideoTimeFormat = (time: number | undefined) => {
  if (!time) {
    return '0:00';
  }
  const miliseconds = _.round(time - Math.floor(time), 1) * 10;
  const seconds = Math.floor(time % 60);
  const minutes = Math.floor(time / 60) % 60;
  const hours = Math.floor(time / 3600);
  if (hours === 0) {
    return `${minutes}:${leadingZeroFormatter.format(seconds)}.${miliseconds}`;
  }
  return `${hours}:${leadingZeroFormatter.format(
    minutes,
  )}:${leadingZeroFormatter.format(seconds)}`;
};

export const getFfmpegTimeFormat = (time: number | undefined) => {
  if (!time) {
    return '00:00:00';
  }
  const miliseconds = _.round(time - Math.floor(time), 3) * 1000;
  const seconds = Math.floor(time % 60);
  const minutes = Math.floor(time / 60) % 60;
  const hours = Math.floor(time / 3600);
  if (hours === 0) {
    return `${leadingZeroFormatter.format(hours)}:${leadingZeroFormatter.format(
      minutes,
    )}:${leadingZeroFormatter.format(seconds)}${
      miliseconds > 0 ? '.' + miliseconds : ''
    }`;
  }
  return `${leadingZeroFormatter.format(hours)}:${leadingZeroFormatter.format(
    minutes,
  )}:${leadingZeroFormatter.format(seconds)}${
    miliseconds > 0 ? '.' + millisecondsFormatter.format(miliseconds) : ''
  }`;
};

const UPLOAD_FILENAME_FALLBACK = 'upload';

const sanitizeBase = (name: string): string => {
  const sanitized = name
    .replace(/[^\p{L}\p{N}._-]+/gu, '_')
    .replace(/_+/g, '_')
    .replace(/^[_.-]+|[_.-]+$/g, '');
  return sanitized.length > 0 ? sanitized : UPLOAD_FILENAME_FALLBACK;
};

export const sanitizeUploadFilename = (filename: string): string => {
  if (!filename) return UPLOAD_FILENAME_FALLBACK;
  const lastDot = filename.lastIndexOf('.');
  if (lastDot <= 0 || lastDot === filename.length - 1) {
    return sanitizeBase(filename);
  }
  const base = sanitizeBase(filename.substring(0, lastDot));
  const ext = filename.substring(lastDot + 1).replace(/[^\p{L}\p{N}]+/gu, '');
  return ext ? `${base}.${ext}` : base;
};
